// Prescriptive trip-planning guidance — written for non-travelers who need
// concrete direction: "how many days, why that many, what most people do,
// here's the proven itinerary." Surfaced on the destination + dates step
// so the user gets guidance BEFORE they commit to dates.
//
// Each city has a duration recommendation with detailed reasoning grounded
// in real common travel patterns + named-restaurant recommendations that
// exist (verify hours / book in advance). Multi-city patterns are based on
// itineraries widely reported by travel writers + Reddit r/travel + Time
// Out city guides as the standard / recommended approaches.
//
// Pricing in $ / € is 2024-2025 typical — VERIFY closer to your dates
// (especially flight + train fares which fluctuate weekly). Restaurant
// names + hotels mentioned should also be re-checked for current status.
//
// Portugal is the deepest section since it's the case-study trip.

// ── Per-city duration guidance ──
// minDays: bare minimum to not feel rushed
// sweetSpot: the [low, high] range MOST travelers pick — and the optimal
// maxDays: when you start running out of must-dos
// reasoning: WHY these specific numbers — what fits in each day
export const CITY_DURATION = {
  // Portugal (deepest)
  Lisbon: {
    minDays: 2,
    sweetSpot: [3, 4],
    optimal: 4,
    maxDays: 6,
    reasoning: {
      day1: 'Arrival + Alfama walk + first dinner. You\'re jet-lagged, the city has hills. Don\'t plan more than this.',
      day2: 'Belém (Tower, Jerónimos Monastery, MAAT, Pastéis de Belém) is a half-day. Pair with Time Out Market for lunch and rooftop bars in Bairro Alto for evening.',
      day3: 'Sintra is a FULL day — Pena Palace + Quinta da Regaleira + Cabo da Roca + lunch at a quinta. This is the day everyone says is the best of their trip. Hire a driver (€100-130).',
      day4: 'LX Factory + Príncipe Real shopping in the morning, then Cascais beach afternoon (40-min train), Fado dinner in Alfama. This is the "round it out" day.',
      day5: 'Optional day for surfing in Ericeira, Évora day trip (Alentejo wine country), or just a slow Lisbon morning.',
      whyNotMore: 'After Day 4, you\'ll start repeating neighborhoods. Lisbon is meant to be paired — most travelers go on to Porto, Algarve, or the Azores.',
    },
    commonMistakes: [
      'Trying to do Sintra + Belém in the same day (impossible — both are half-days minimum)',
      'Putting Sintra on arrival day (you\'ll be exhausted)',
      'Skipping rooftop bars (Park, TOPO, Sky Bar — these are quintessential Lisbon)',
      'Eating dinner before 9 PM (locals eat late — restaurants are dead before 8:30)',
    ],
    sources: [
      'Rick Steves Portugal (3-4 days Lisbon recommended)',
      'r/travel + r/portugal consensus (Sintra is a full day, not half)',
      'Lonely Planet Lisbon (4-day "perfect itinerary")',
      'NYT 36 Hours in Lisbon (Belém + Alfama + Bairro Alto core)',
    ],
  },
  Porto: {
    minDays: 2,
    sweetSpot: [2, 3],
    optimal: 3,
    maxDays: 4,
    reasoning: {
      day1: 'Arrival + Ribeira waterfront walk + cable car down to Vila Nova de Gaia for a port flight. End at Crystal Palace Gardens for sunset.',
      day2: 'Douro Valley FULL day (wine country tour, 2-3 quintas, lunch with river views). The reason most people add Porto.',
      day3: 'Lello bookstore (morning, before crowds), São Bento azulejos, Clérigos Tower, Foz do Douro afternoon for beach + Cafeína dinner.',
      whyNotMore: 'After Day 3, Porto starts feeling small. Most add a Douro Valley overnight (1-2 nights at Quinta Nova or Quinta do Bomfim) rather than another Porto day.',
    },
    commonMistakes: [
      'Skipping the Douro Valley day trip (it\'s why you came to Porto)',
      'Booking port tasting at the first cellar you see (the discount ones — Sandeman, Cálem, Graham\'s — are better)',
      'Not booking Livraria Lello in advance (line is 2+ hours otherwise)',
      'Eating francesinha at a hotel bar (go to Cafe Santiago or Lado B)',
    ],
    sources: [
      'Rick Steves Portugal (2-3 days Porto + Douro day trip)',
      'r/travel "How long in Porto?" — consensus 2-3 nights',
      'Conde Nast Traveler Porto guide (Douro Valley = the reason to go)',
      'Time Out Porto best francesinha (Cafe Santiago, Lado B top picks)',
    ],
  },
  // Spain
  Barcelona: {
    minDays: 3,
    sweetSpot: [3, 4],
    optimal: 4,
    maxDays: 5,
    reasoning: {
      day1: 'Arrival + Gothic Quarter walk + tapas in El Born for first night. Light schedule.',
      day2: 'Sagrada Família (book the 9 AM entry — empty + best light) + Passeig de Gràcia (Casa Batlló, La Pedrera) + Park Güell at sunset.',
      day3: 'Beach morning (Barceloneta) + Boqueria Market lunch + Picasso Museum afternoon + Old Town tapas crawl evening.',
      day4: 'Montserrat day trip (1h train) OR Sitges beach town (40 min) OR Costa Brava (1.5h to Tossa de Mar). Sintra-level day that rounds out the trip.',
      whyNotMore: 'After Day 4 Barcelona starts repeating. Add Ibiza, Madrid, or Valencia instead.',
    },
    commonMistakes: [
      'Sagrada Família without pre-booking (sold out same-day in season)',
      'Las Ramblas dinner (the worst tourist trap restaurants in Europe — walk 3 blocks in any direction)',
      'Park Güell mid-day (200 people queue for the iconic shot)',
      'Skipping a day trip (Montserrat or Sitges is one of the best parts)',
    ],
    sources: [
      'Rick Steves Spain (3-4 days Barcelona standard)',
      'Lonely Planet Barcelona "perfect 4-day itinerary"',
      'r/travel "How many days in Barcelona?" — consensus 3-4',
      'NYT 36 Hours Barcelona (Gothic + Sagrada + Park Güell core)',
    ],
  },
  Madrid: {
    minDays: 2,
    sweetSpot: [3, 3],
    optimal: 3,
    maxDays: 4,
    reasoning: {
      day1: 'Plaza Mayor + Mercado San Miguel for tapas lunch + Royal Palace afternoon + flamenco show evening.',
      day2: 'Prado + Reina Sofía morning (Picasso\'s Guernica) + Retiro Park lunch + Salamanca shopping + late-night Malasaña.',
      day3: 'Toledo OR Segovia day trip (both 30 min by AVE train, perfect day trips with castle + cathedral).',
      whyNotMore: 'Madrid is a 3-day city. The food scene is endless but the must-sees fit in 3.',
    },
    commonMistakes: [
      'Trying to do Prado in 1 hour (give it 2-3, focus on Velázquez + Goya)',
      'Eating dinner at 7 PM (Madrid eats at 10 PM — restaurants close 12 AM)',
      'Skipping the Toledo day trip (genuinely magical, easier than Sintra)',
    ],
    sources: [
      'Rick Steves Madrid (3 days standard)',
      'r/travel + r/madrid consensus (3 days enough)',
      'NYT 36 Hours in Madrid (Prado + Plaza Mayor + tapas core)',
    ],
  },
  // Italy
  Rome: {
    minDays: 3,
    sweetSpot: [3, 4],
    optimal: 4,
    maxDays: 5,
    reasoning: {
      day1: 'Trastevere evening tapas crawl + Pantheon at night (free, magical) — light day after travel.',
      day2: 'Vatican Museums + Sistine Chapel EARLY (7:30 AM "open the gates" tour) + St Peter\'s + lunch in Prati. Free afternoon.',
      day3: 'Colosseum + Forum + Palatine (combo ticket, full morning) + Aventine Hill sunset + Trastevere dinner.',
      day4: 'Galleria Borghese (book ahead, 2h) + Piazza Navona + Spanish Steps + Pompeii or Tivoli day trip optional.',
      whyNotMore: 'Rome\'s "big 3" (Vatican, Colosseum, Trastevere) are 3 days. Day 4 is for Borghese + a day trip. Beyond that you\'re padding.',
    },
    commonMistakes: [
      'Vatican without skip-the-line (3+ hour wait otherwise)',
      'Eating near tourist sights (Trevi Fountain pizza = €25 garbage; walk 5 blocks)',
      'Colosseum without Forum/Palatine combo (you pay for one and miss the other)',
      "Skipping Aventine Hill (locals' sunset spot, free, walkable from Colosseum)",
    ],
    sources: [
      'Rick Steves Rome (3-4 days minimum)',
      'r/travel "First time Rome" — consensus 4 nights',
      'Lonely Planet Rome ("perfect 4 days")',
      'The Roman Guy + ContextTravel tour guides (4 days = Vatican + Forum + Borghese + neighborhoods)',
    ],
  },
  'Amalfi Coast': {
    minDays: 3,
    sweetSpot: [3, 4],
    optimal: 4,
    maxDays: 5,
    reasoning: {
      day1: 'Arrival in Positano + dinner with the cliff view + early night.',
      day2: 'Boat day along the coast (Positano → Praiano → Furore caves → Amalfi → Atrani), lunch on the water. THE Amalfi experience.',
      day3: 'Path of the Gods hike (Bomerano → Nocelle, 3-4h, moderate) + lunch in Nocelle + afternoon swim.',
      day4: 'Pompeii morning (1.5h drive) + Sorrento lunch + back to Positano evening for dinner.',
      whyNotMore: 'Amalfi is small. After 4 days you\'ve done the main beaches + hikes. Add Capri (boat + funicular up + lunch) as a 5th-day option.',
    },
    commonMistakes: [
      'Driving the Amalfi Coast road yourself (terrifying, no parking — hire a driver or use SITA bus)',
      'Booking a hotel in Amalfi town (less charming than Positano or Praiano — same price)',
      'Eating dinner on the main strip (walk uphill — the family-run spots are 50% the price)',
      'Skipping Path of the Gods (the hike is THE memory most people bring back)',
    ],
    sources: [
      'Rick Steves Italy (3-4 days Amalfi recommended)',
      'r/travel + r/italytravel ("How many days Amalfi?" consensus 3-5)',
      'Conde Nast Amalfi guide (Positano base, boat day = highlight)',
      'AFAR Magazine "Path of the Gods" trail report',
    ],
  },
  // France
  Paris: {
    minDays: 3,
    sweetSpot: [4, 5],
    optimal: 5,
    maxDays: 7,
    reasoning: {
      day1: 'Île Saint-Louis at sunset + dinner at a Marais bistro — light arrival day.',
      day2: 'Louvre morning (3h with Mona Lisa) + Tuileries walk + Champs-Élysées afternoon + Eiffel Tower at sunset.',
      day3: 'Versailles full day (RER train 45 min, palace + gardens by bike, lunch by the Grand Canal).',
      day4: 'Musée d\'Orsay + Saint-Germain coffee + Latin Quarter + Seine cruise + Montmartre dinner.',
      day5: 'Pere Lachaise + Belleville + canal Saint-Martin + a chef\'s tasting menu dinner.',
      whyNotMore: 'After 5 days you start repeating neighborhoods. Add a Loire Valley castles day trip (TGV to Tours, 1h) for day 6-7.',
    },
    commonMistakes: [
      'Louvre without timed entry (3+ hour wait otherwise)',
      'Versailles in 2 hours (it\'s a FULL day — rent a bike, picnic by the canal)',
      'Eating in the 7th near Eiffel Tower (tourist prices) — walk to rue Cler instead',
      'Champs-Élysées restaurants in general (tourist trap factor 10)',
    ],
    sources: [
      'Rick Steves Paris (4-5 days standard, 7 for deep dive)',
      'r/travel + r/paris consensus (4-5 days first trip)',
      'NYT 36 Hours Paris series (rotating neighborhood deep dives)',
      'David Lebovitz "Living the Sweet Life in Paris" food guide',
    ],
  },
  // UK
  London: {
    minDays: 3,
    sweetSpot: [4, 5],
    optimal: 5,
    maxDays: 7,
    reasoning: {
      day1: 'Pub dinner + early sleep. London is huge — pace yourself.',
      day2: 'Tower of London morning + Borough Market lunch + Tate Modern + South Bank walk + West End theater evening.',
      day3: 'British Museum (free, give it 3h) + Covent Garden lunch + Westminster + Big Ben + St James\' Park + Mayfair dinner.',
      day4: 'Notting Hill + Portobello Market morning + Hyde Park afternoon + Kensington/V&A + Soho dinner.',
      day5: 'Day trip to Oxford, Cambridge, Bath, or Windsor Castle (all 1-2h by train).',
      whyNotMore: 'London genuinely supports 7 days but most people fit it in 5. Day 6-7 should be Cotswolds villages.',
    },
    commonMistakes: [
      'Tower of London without Yeoman Warder tour (the funny + sad medieval stories make it)',
      'Skipping a West End play (London theater is unmatched)',
      'Eating in Leicester Square (tourist hellzone)',
      'Not using contactless tap on the tube (daily caps automatically — no need for travelcards)',
    ],
    sources: [
      'Rick Steves London (4-5 days standard)',
      'r/travel + r/london ("How many days London?" — 5 nights consensus)',
      'Time Out London "perfect weekend" + extended guides',
      'NYT 36 Hours London (rotating)',
    ],
  },
  // Greece
  Santorini: {
    minDays: 2,
    sweetSpot: [3, 4],
    optimal: 3,
    maxDays: 4,
    reasoning: {
      day1: 'Fly to Santorini + transfer to Oia + sunset walk in the village + dinner with the caldera view.',
      day2: 'Hike Fira → Oia (3h coastal path) OR boat tour to the volcano + hot springs + lunch in Thirassia.',
      day3: 'Wine tasting at 3 caldera-view wineries + sunset at Akrotiri lighthouse (avoid Oia crowds).',
      whyNotMore: 'Santorini is small. After 3 days you\'ve seen it all. Add Naxos or Milos as a 2nd Greek island day-tripping by ferry.',
    },
    commonMistakes: [
      'Trying to see the Oia sunset from inside the Oia crowd (stand at the windmill 30 min early or watch from the boat tour)',
      'Staying in Fira (party hostels, not the postcard view — stay in Oia or Imerovigli)',
      'Beach time at Red Beach (overcrowded, slippery — go to Perissa or Vlychada instead)',
    ],
    sources: [
      'r/Greece + r/santorini "how long" consensus (3-4 nights)',
      'Travel + Leisure Santorini guide (Oia / Imerovigli > Fira)',
      'Conde Nast Greece island guide',
    ],
  },
  // Iceland
  Reykjavik: {
    minDays: 3,
    sweetSpot: [4, 6],
    optimal: 5,
    maxDays: 7,
    reasoning: {
      day1: 'Arrival + Blue Lagoon afternoon (book ahead) + dinner downtown Reykjavik.',
      day2: 'Golden Circle full day (Thingvellir, Geysir, Gullfoss, Secret Lagoon) — 8 hours of natural wonders.',
      day3: 'South Coast (Seljalandsfoss, Skógafoss, black sand beach Reynisfjara, Vík) — 10-hour day, worth it.',
      day4: 'Snæfellsnes Peninsula (Kirkjufell mountain, lava fields, Stykkishólmur fishing village) — slower-paced day.',
      day5: 'Whale watching morning + Northern Lights chase tour evening (Sept-March).',
      whyNotMore: 'After 5 days you\'re into the more remote regions (Westfjords, Highlands) which need a 4WD + camping setup.',
    },
    commonMistakes: [
      'Driving in winter without a 4WD (treacherous; spring for a Land Cruiser if Oct-April)',
      'Skipping the Blue Lagoon because "it\'s touristy" (it\'s great — book the morning slot)',
      'Trying to see Northern Lights from downtown (need to drive out of city light pollution)',
    ],
    sources: [
      'Lonely Planet Iceland (5-7 days for the Ring Road sample)',
      'r/VisitingIceland "how many days" (5+ for full experience)',
      'Guide to Iceland route planner (Golden Circle + South Coast staples)',
      'NYT Travel "Iceland in a week" series',
    ],
  },
};

// ── Multi-city itinerary patterns ──
// For each common multi-city pair (matching the destinations table bundle
// names like "Lisbon + Porto"), prescribe 2-4 proven itineraries with
// per-city allocations + reasoning.
export const MULTI_CITY_PATTERNS = {
  'Lisbon + Porto': {
    overview: 'Portugal\'s top two cities, connected by a beautiful 3-hour train. This is the most common multi-city Portugal itinerary — and for good reason: Lisbon\'s scale + Porto\'s charm complement each other perfectly.',
    transport: 'Lisbon → Porto: Alfa Pendular train, 3h, ~€32/pp. Book at cp.pt 7+ days ahead.',
    patterns: [
      {
        name: 'First-timer (recommended)',
        nights: { Lisbon: 4, Porto: 3 },
        totalNights: 7,
        why: 'Standard 7-night Portugal trip — gives Lisbon room for Sintra day trip + Belém + 2 evenings of Bairro Alto, then 3 Porto nights for Douro Valley + the city. Most popular for a reason.',
        whoFor: 'First time in Portugal · couples + groups · prefer balance over depth',
        sample: 'Day 1: Lisbon arrival → Alfama walk → dinner. Day 2: Belém + Time Out Market + rooftop bars. Day 3: Sintra full day. Day 4: LX Factory + Cascais beach + Fado dinner. Day 5: Lisbon → Porto by train (arrive PM) → Ribeira walk + port flight. Day 6: Douro Valley full day. Day 7: Lello bookstore + São Bento + Foz beach + farewell dinner.',
      },
      {
        name: 'Lisbon-heavy (foodie focus)',
        nights: { Lisbon: 5, Porto: 2 },
        totalNights: 7,
        why: 'Lisbon\'s food scene is deeper than Porto\'s — more Michelin spots, more market culture. This pattern adds a 5th Lisbon day for Évora (Alentejo wine) or Ericeira surfing + extra Bairro Alto crawls. Trades a Porto day.',
        whoFor: 'Couples · foodies · slower travelers who don\'t want to move hotels often',
        sample: 'Days 1-4: same as first-timer. Day 5: Évora day trip OR Ericeira surf morning + Belcanto fine dining. Day 6: Lisbon → Porto → Crystal Palace sunset + port tasting. Day 7: Douro Valley → return same day.',
      },
      {
        name: 'Porto-heavy (wine + small-city vibe)',
        nights: { Lisbon: 3, Porto: 4 },
        totalNights: 7,
        why: 'Porto is the wine country anchor. This pattern compresses Lisbon to the essentials (arrival + Sintra + Belém) and gives Porto extra room for a Douro Valley OVERNIGHT (Quinta do Bomfim or Quinta Nova) — the 5-star upgrade most travelers don\'t do.',
        whoFor: 'Wine lovers · second-time Portugal travelers · couples wanting a romantic anchor',
        sample: 'Day 1: Lisbon arrival → Alfama walk. Day 2: Belém + Time Out + Bairro Alto. Day 3: Sintra full day. Day 4: Lisbon → Porto (arrive PM). Day 5: Douro Valley → check into quinta overnight. Day 6: Return to Porto → Foz beach + Cafe Santiago francesinha. Day 7: Livraria Lello + farewell dinner at Antiqvvm.',
      },
      {
        name: '10-day deep dive (add Algarve)',
        nights: { Lisbon: 4, Porto: 3, Algarve: 3 },
        totalNights: 10,
        why: 'A third stop down south unlocks Portugal\'s beach culture. Algarve in Sept = warm water (70-72°F) + smaller crowds after the August Europeans leave. The "I have 10 nights and want it all" pattern.',
        whoFor: 'Longer trips · beach + city combos · families · anniversary trips',
        sample: 'Days 1-4: Lisbon as standard. Days 5-7: Porto as standard. Day 8: Fly Porto → Faro direct (TAP, ~1h45m, ~$100/pp) OR train Porto → Lisbon (3h) then Lisbon → Faro train (3h, the longer day). Days 8-10: Algarve beach + Benagil cave boat tour + Cabo de São Vicente cliffs + farewell.',
      },
    ],
  },

  'Barcelona + Ibiza': {
    overview: 'City + beach. Barcelona for culture and tapas, Ibiza for the Mediterranean fantasy. Best Sept-Oct when Ibiza crowds thin but water is still warm.',
    transport: 'Barcelona → Ibiza: Vueling flight 50 min ~€60-100 OR ferry 4.5h ~€60. Flight is the default.',
    patterns: [
      {
        name: 'First-timer (recommended)',
        nights: { Barcelona: 4, Ibiza: 3 },
        totalNights: 7,
        why: 'Standard mix — 4 days Barcelona covers Sagrada + Park Güell + day trip + tapas crawls, then 3 days Ibiza for boat day + Es Vedrà + 1 club night (or skip).',
        whoFor: 'First Spain trip · couples · 20s-30s · wants both culture + beach',
        sample: 'Days 1-4: Barcelona (Gothic + Sagrada + beach + Montserrat). Day 5: Fly Ibiza → check in San Antoni → sunset at Café del Mar. Day 6: Catamaran cruise to Cala Comte. Day 7: Es Vedrà hike + farewell dinner at Es Boldadó.',
      },
      {
        name: 'Beach-heavy (chill mode)',
        nights: { Barcelona: 3, Ibiza: 4 },
        totalNights: 7,
        why: 'For travelers who want Ibiza decompression after Barcelona\'s intensity. Extra Ibiza day means 1 day for clubs (Ushuaïa, Pacha), 1 for hidden coves (Cala Salada, Cala Llonga).',
        whoFor: 'Beach lovers · those skipping nightlife · couples',
        sample: 'Days 1-3: Barcelona compressed. Days 4-7: Ibiza spread — boat day, club night, beach hopping, sunset at Es Vedrà.',
      },
    ],
  },

  'Amalfi Coast + Rome': {
    overview: 'Classic Italy combo. Rome for history + food intensity, Amalfi for the cliffside fantasy. Sept-Oct is the sweet spot — warm sea, no August crowds.',
    transport: 'Rome → Amalfi: Frecciarossa train Rome → Naples (1h, €30) + SITA bus or private transfer Naples → Positano (1.5h, €15 bus or €130 driver).',
    patterns: [
      {
        name: 'First-timer (recommended)',
        nights: { Rome: 4, 'Amalfi Coast': 3 },
        totalNights: 7,
        why: 'Rome\'s big 3 (Vatican, Colosseum, Trastevere) need 4 days. Amalfi can be done in 3 (boat day + hike day + chill day). Most common Italy trip pattern.',
        whoFor: 'First Italy trip · couples · 30s-50s · history + beach',
        sample: 'Days 1-4: Rome (Vatican early entry, Colosseum, Trastevere crawl, Borghese). Day 5: Train Rome → Naples → Positano. Day 6: Boat day along coast. Day 7: Path of the Gods hike + farewell.',
      },
      {
        name: 'Amalfi-heavy',
        nights: { Rome: 3, 'Amalfi Coast': 4 },
        totalNights: 7,
        why: 'Skip the Borghese day in Rome (controversial — most people regret this), add an Amalfi day for Capri or Pompeii.',
        whoFor: 'Returning Italy travelers · beach lovers',
        sample: 'Days 1-3: Rome essentials. Day 4: Transit + Positano dinner. Days 5-7: Amalfi — boat, hike, Capri day, Pompeii day.',
      },
      {
        name: 'Long version (add Florence)',
        nights: { Rome: 3, Florence: 3, 'Amalfi Coast': 4 },
        totalNights: 10,
        why: 'Florence + Tuscany + Amalfi + Rome = the bucket-list Italy trip. Florence by train is 1.5h from Rome. Get the Florence → Amalfi pattern right (back through Rome, NOT down via Naples).',
        whoFor: 'Bucket-list trip · 10+ nights · honeymoon',
        sample: 'Days 1-3: Rome. Days 4-6: Florence + day trip to Tuscany wineries. Days 7-10: Amalfi (transit through Rome, then to Naples + Positano).',
      },
    ],
  },

  'London + Cotswolds': {
    overview: 'City + countryside. London for culture + food + theater, Cotswolds for honey-stone villages + pubs + walks. Train + rental car combo.',
    transport: 'London → Cotswolds: Train to Moreton-in-Marsh (1.5h, £25) + RENT A CAR there (no Uber, sparse buses) OR take a private driver day-trip out and back.',
    patterns: [
      {
        name: 'First-timer (recommended)',
        nights: { London: 5, Cotswolds: 2 },
        totalNights: 7,
        why: 'London needs 5 days for must-sees + a day trip slot. Cotswolds is a 2-night escape — Bibury, Castle Combe, Stow-on-the-Wold, pub dinners by the fire.',
        whoFor: 'First UK trip · couples · prefer balanced city + countryside',
        sample: 'Days 1-5: London (Tower, museum, theater, Notting Hill, day trip). Days 6-7: Train + rental car → Cotswolds (Stow base, drive between villages, pub dinner at The Royal Oak).',
      },
      {
        name: 'Cotswolds-heavy',
        nights: { London: 4, Cotswolds: 3 },
        totalNights: 7,
        why: 'Adds a 3rd Cotswolds night = time for Bath day trip from your village base + a slow morning at a thatched-roof B&B.',
        whoFor: 'Quiet trips · honeymoon · prefer countryside over city',
        sample: 'Days 1-4: London compressed. Days 5-7: Cotswolds + Bath day trip.',
      },
    ],
  },

  'Nice + Monaco': {
    overview: 'Côte d\'Azur — Nice as the base, Monaco as the day trip. Add Cannes/Antibes/Eze for more.',
    transport: 'Nice → Monaco: Train 25 min, €5/pp. So close you don\'t need to switch hotels — most do it as a day trip.',
    patterns: [
      {
        name: 'Nice base (single hotel)',
        nights: { Nice: 5 },
        totalNights: 5,
        why: 'Honestly, you don\'t need to split. Monaco + Eze + Cannes + Antibes are all easy day trips from a Nice hotel. Saves the hotel-change hassle.',
        whoFor: 'Most travelers · couples · families',
        sample: 'Day 1: Promenade des Anglais + Vieux Nice. Day 2: Monaco day (Monte Carlo + Princess Grace tour). Day 3: Antibes + Picasso Museum. Day 4: Cannes + boat to Lerins Islands. Day 5: Eze village + Saint-Paul-de-Vence.',
      },
      {
        name: 'Multi-base (Monaco overnight)',
        nights: { Nice: 3, Monaco: 2 },
        totalNights: 5,
        why: 'Monaco is overpriced but staying overnight gets you the Casino de Monte Carlo at night without rushing the train back.',
        whoFor: 'Luxury travelers · big-budget · novelty seekers',
        sample: 'Days 1-3: Nice. Days 4-5: Monaco — Casino, Le Louis XV dinner, Princess Grace tour.',
      },
    ],
  },

  'Santorini + Athens': {
    overview: 'Most people do Athens FIRST then Santorini — fly to Athens, see the Acropolis, then ferry/fly to Santorini for the relaxing end.',
    transport: 'Athens → Santorini: Sky Express flight 50 min ~€80 OR Blue Star ferry 5-8h ~€60. Flight is the default unless you want the slow ferry experience.',
    patterns: [
      {
        name: 'First-timer (recommended)',
        nights: { Athens: 3, Santorini: 4 },
        totalNights: 7,
        why: 'Athens has 3 anchor days (Acropolis, museums, Plaka). Santorini gets 4 — boat day, Oia exploration, wine tour, beach day.',
        whoFor: 'First Greece trip · couples · history + beach',
        sample: 'Days 1-3: Athens (Acropolis early, Plaka tavernas, Sounion day trip). Day 4: Fly Athens → Santorini → Oia sunset. Days 5-7: Santorini (boat to volcano, wine tour, beach + Akrotiri).',
      },
      {
        name: 'Island hopping (longer)',
        nights: { Athens: 2, Santorini: 3, Naxos: 2 },
        totalNights: 7,
        why: 'Add Naxos for the authentic island vibe (no cliffs, no crowds, real local life). Ferry Santorini → Naxos is 90 min.',
        whoFor: 'Returning Greece travelers · beach lovers · slow travelers',
        sample: 'Days 1-2: Athens essentials. Days 3-5: Santorini. Days 6-7: Naxos (Chora village, Plaka beach).',
      },
    ],
  },
};

export function getCityDuration(city) {
  if (!city) return null;
  // Bundled — try primary first
  const primary = city.split(' + ')[0].trim();
  return CITY_DURATION[primary] || CITY_DURATION[city] || null;
}

export function getMultiCityPattern(bundleName) {
  if (!bundleName) return null;
  return MULTI_CITY_PATTERNS[bundleName] || null;
}
